import React, { useState, useMemo } from 'react';
import { Layout, Typography, Row, Col, Card, Slider, Tag, Space } from 'antd';
import {
  RiseOutlined, ThunderboltOutlined, TrophyOutlined, WarningOutlined,
  SearchOutlined, FileTextOutlined, PhoneOutlined, TeamOutlined,
  CheckCircleFilled, LineChartOutlined, DatabaseOutlined,
  LockOutlined, RocketOutlined, BulbOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

// ── Constants ────────────────────────────────────────────────────────────────

const MCKINSEY_HRS_DAY = 1.8;
const DAYS_MONTH = 22;
const HRS_FTE_MONTH = 160;

const TEAM = {
  size: 35,
  costPerYear: 1654000,       // €, coste empresa con ×1.32
  hourlyCost: 24.6,           // €/h
  revenueRoles: 23,           // Construction Mgrs + Planners (los que mueven la aguja)
};

const BUSINESS = {
  projects: 110,              // proyectos cerrados/año
  conversion: 0.18,           // 18 %
  ticket: 100,                // K€ ticket medio
  leadsPerYear: 611,          // 110 / 0.18
  revenueKEUR: 11000,         // 11 M€
  budgetsMonth: 40,
  budgetCost: 150,            // €/presupuesto
};

const OPEX_MONTHLY = [
  { item: 'Cloud Run (3 servicios)', value: 85 },
  { item: 'Cloud SQL Postgres',      value: 50 },
  { item: 'MongoDB Atlas (M10)',     value: 57 },
  { item: 'Claude API (uso intensivo agentes)', value: 400 },
  { item: 'Vertex AI (Gemini)',      value: 150 },
  { item: 'LangSmith (Plus)',        value: 30 },
  { item: 'Tavily (web search)',     value: 30 },
  { item: 'Otros (storage, traffic)', value: 30 },
];
const OPEX_TOTAL_MONTH = OPEX_MONTHLY.reduce((s, x) => s + x.value, 0); // ≈ 832

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtEUR = (n: number) => n.toLocaleString('es-ES', { maximumFractionDigits: 0 }) + ' €';
const fmtKEUR = (n: number) => (n / 1000).toLocaleString('es-ES', { maximumFractionDigits: n < 100 ? 0 : 0 }) + ' K€';
const fmtMEUR = (n: number) => (n / 1000).toLocaleString('es-ES', { maximumFractionDigits: 2 }) + ' M€';
const fmtPct  = (n: number) => n.toLocaleString('es-ES', { maximumFractionDigits: 1 }) + ' %';

// ── Bottleneck cards (diagnóstico) ───────────────────────────────────────────

interface Bottleneck {
  icon: React.ReactNode;
  title: string;
  metric: string;
  metricLabel: string;
  body: string;
  color: string;
  bg: string;
}

const BOTTLENECKS: Bottleneck[] = [
  {
    icon: <SearchOutlined />,
    title: 'Búsqueda multicanal',
    metric: '1.386 h',
    metricLabel: 'al mes buscando información',
    body: 'WhatsApp, Slack, Monday, Plataforma. Según McKinsey, un knowledge worker invierte 1,8 h/día buscando. Con 35 personas son ≈5 FTE atadas a esto cada mes.',
    color: '#cf1322', bg: '#fff1f0',
  },
  {
    icon: <FileTextOutlined />,
    title: 'Presupuestos pre-venta',
    metric: '480 / año',
    metricLabel: 'presupuestos generados',
    body: '40 presupuestos al mes × 2h × 150€ coste. Solo 110 acaban en cliente — el resto es esfuerzo en leads que no se convierten porque no están cualificados objetivamente.',
    color: '#d46b08', bg: '#fff7e6',
  },
  {
    icon: <PhoneOutlined />,
    title: 'Cualificación subjetiva',
    metric: '19 / 0',
    metricLabel: 'llamadas frías → conversaciones útiles',
    body: 'Sin score objetivo de lead, el comercial decide a quién llamar por intuición. El día que se hicieron 19 llamadas, ninguna pudo hablar — y probablemente había un lead caliente sin contactar.',
    color: '#531dab', bg: '#f9f0ff',
  },
  {
    icon: <TeamOutlined />,
    title: 'Reuniones sin proceso',
    metric: '0 analíticas',
    metricLabel: 'sobre lo que hablamos con clientes',
    body: 'Cada Planner / CM / Account Mgr documenta a su manera. Información valiosa se queda en notas, mails y memorias. No hay datos para decidir qué funciona y qué no.',
    color: '#003a8c', bg: '#e6f4ff',
  },
];

// ── Content (reusable inside slides too) ─────────────────────────────────────

export const BusinessCaseContent: React.FC = () => {
  // Inputs interactivos
  const [searchRecoveryPct, setSearchRecoveryPct] = useState(60);
  const [conversionUplift, setConversionUplift] = useState(30);

  const calc = useMemo(() => {
    const searchHrs = MCKINSEY_HRS_DAY * DAYS_MONTH * TEAM.size;             // 1.386
    const recoveredHrs = searchHrs * (searchRecoveryPct / 100);
    const recoveredFTE = recoveredHrs / HRS_FTE_MONTH;

    const newConv = BUSINESS.conversion * (1 + conversionUplift / 100);
    const newProj = BUSINESS.leadsPerYear * newConv;
    const dProjConv = newProj - BUSINESS.projects;
    const dRevConv  = dProjConv * BUSINESS.ticket;   // K€

    const projPerRevFTE = BUSINESS.projects / TEAM.revenueRoles; // 4.78 proj/rev-FTE/año
    const dProjCap = recoveredFTE * projPerRevFTE;
    const dRevCap  = dProjCap * BUSINESS.ticket;

    const dRevTotal = dRevConv + dRevCap;
    const newRev    = BUSINESS.revenueKEUR + dRevTotal;
    const growthPct = (dRevTotal / BUSINESS.revenueKEUR) * 100;

    const opexYear = OPEX_TOTAL_MONTH * 12;
    const oneCMCost = 39600; // €/año
    const opexAsCMPct = (opexYear / oneCMCost) * 100;

    const dRevYear1KEUR = dRevTotal * 0.5;  // ramp-up año 1
    const dRevYear2KEUR = dRevTotal * 0.85;
    const dRevYear3KEUR = dRevTotal * 1.0;
    const cumRev3y = dRevYear1KEUR + dRevYear2KEUR + dRevYear3KEUR;

    return {
      searchHrs, recoveredHrs, recoveredFTE,
      newConv, newProj, dProjConv, dRevConv,
      projPerRevFTE, dProjCap, dRevCap,
      dRevTotal, newRev, growthPct,
      opexYear, opexAsCMPct,
      dRevYear1KEUR, dRevYear2KEUR, dRevYear3KEUR, cumRev3y,
    };
  }, [searchRecoveryPct, conversionUplift]);

  return (
    <div style={{ height: '100%', overflowY: 'auto', background: '#f5f5f5' }}>
      <div style={{ padding: '32px 48px', maxWidth: 1280, margin: '0 auto', width: '100%' }}>

        {/* ── HERO ──────────────────────────────────────────────────────────── */}
        <div style={{
          background: 'linear-gradient(135deg, #0c1e4a 0%, #1e3a8a 50%, #3b82f6 100%)',
          borderRadius: 16, padding: '40px 48px', color: '#fff', marginBottom: 32,
          boxShadow: '0 12px 40px rgba(30,58,138,0.2)',
        }}>
          <Tag style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontWeight: 600, fontSize: 11, padding: '3px 12px', letterSpacing: 0.5, marginBottom: 18 }}>
            BUSINESS CASE · CUBICUP BRAIN
          </Tag>
          <Title level={1} style={{ color: '#fff', margin: 0, fontSize: 38, fontWeight: 800, lineHeight: 1.15, letterSpacing: -0.5 }}>
            De {fmtMEUR(BUSINESS.revenueKEUR)} a {fmtMEUR(calc.newRev)}
            <span style={{ display: 'block', fontSize: 22, fontWeight: 500, color: 'rgba(255,255,255,0.85)', marginTop: 8 }}>
              sin contratar a nadie más, y construyendo un activo que la competencia no podrá copiar.
            </span>
          </Title>
          <Paragraph style={{ color: 'rgba(255,255,255,0.8)', fontSize: 15, marginTop: 24, marginBottom: 0, maxWidth: 880, lineHeight: 1.7 }}>
            Cubicup Brain no es una herramienta para ahorrar costes. Es la <strong style={{ color: '#fff' }}>palanca operativa</strong> que multiplica la capacidad del equipo actual, y al mismo tiempo el <strong style={{ color: '#fff' }}>moat estratégico</strong> que en 24 meses será nuestra ventaja competitiva más fuerte.
          </Paragraph>
        </div>

        {/* ── DIAGNÓSTICO ───────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 36 }}>
          <Text style={{ fontSize: 12, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 6 }}>
            Diagnóstico
          </Text>
          <Title level={2} style={{ margin: 0, fontSize: 26, fontWeight: 700, marginBottom: 20 }}>
            Hoy estamos dejando dinero — y velocidad — sobre la mesa
          </Title>

          <Row gutter={[16, 16]}>
            {BOTTLENECKS.map((b, i) => (
              <Col key={i} xs={24} md={12} lg={6}>
                <Card
                  style={{ height: '100%', borderRadius: 12, border: `1px solid ${b.color}22`, background: b.bg }}
                  styles={{ body: { padding: '20px 22px', height: '100%', display: 'flex', flexDirection: 'column' } }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: b.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ color: '#fff', fontSize: 16 }}>{b.icon}</span>
                    </div>
                    <Text strong style={{ fontSize: 14, color: '#141414' }}>{b.title}</Text>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 28, fontWeight: 800, color: b.color, lineHeight: 1, display: 'block' }}>{b.metric}</Text>
                    <Text style={{ fontSize: 11, color: '#595959', display: 'block', marginTop: 4 }}>{b.metricLabel}</Text>
                  </div>
                  <Text style={{ fontSize: 12, color: '#595959', lineHeight: 1.65 }}>{b.body}</Text>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* ── PALANCA A — Capacidad (interactiva) ───────────────────────────── */}
        <Card
          style={{ marginBottom: 28, borderRadius: 16, border: '1px solid #d6e4ff', overflow: 'hidden' }}
          styles={{ body: { padding: 0 } }}
        >
          <div style={{ background: 'linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 100%)', padding: '24px 32px', borderBottom: '1px solid #d6e4ff' }}>
            <Space size={10} align="center">
              <div style={{ width: 36, height: 36, borderRadius: 9, background: '#1677ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <RiseOutlined style={{ color: '#fff', fontSize: 17 }} />
              </div>
              <div>
                <Text style={{ fontSize: 11, fontWeight: 700, color: '#1677ff', letterSpacing: 0.6, display: 'block' }}>PALANCA A</Text>
                <Title level={3} style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#003a8c' }}>Capacidad sin hiring proporcional</Title>
              </div>
            </Space>
          </div>

          <div style={{ padding: '28px 32px' }}>
            <Paragraph style={{ fontSize: 14, color: '#595959', lineHeight: 1.75, marginBottom: 24 }}>
              No reemplazamos a nadie — desbloqueamos las horas que el equipo hoy invierte buscando información, escribiendo presupuestos a leads malos y haciendo llamadas frías sin método. Ese tiempo recuperado se convierte directamente en <strong style={{ color: '#141414' }}>capacidad para más proyectos</strong>.
            </Paragraph>

            <Row gutter={32}>
              {/* Inputs */}
              <Col xs={24} md={10}>
                <Text strong style={{ fontSize: 12, color: '#1677ff', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 16 }}>
                  Ajusta las asunciones
                </Text>

                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                    <Text style={{ fontSize: 13, color: '#262626' }}>Tiempo de búsqueda recuperado</Text>
                    <Text strong style={{ fontSize: 18, color: '#1677ff' }}>{searchRecoveryPct} %</Text>
                  </div>
                  <Slider min={20} max={90} value={searchRecoveryPct} onChange={setSearchRecoveryPct} />
                  <Text style={{ fontSize: 11, color: '#8c8c8c' }}>
                    De las 1.386 h/mes que McKinsey dice que el equipo dedica a buscar info, ¿cuánto recuperamos con un Brain centralizado? Default conservador: 60 %.
                  </Text>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                    <Text style={{ fontSize: 13, color: '#262626' }}>Mejora de tasa de conversión</Text>
                    <Text strong style={{ fontSize: 18, color: '#1677ff' }}>+{conversionUplift} %</Text>
                  </div>
                  <Slider min={5} max={80} value={conversionUplift} onChange={setConversionUplift} />
                  <Text style={{ fontSize: 11, color: '#8c8c8c' }}>
                    Hoy convertimos {fmtPct(BUSINESS.conversion * 100)} de leads. Con cualificación objetiva (score por buyer persona + histórico) y respuesta más rápida — ¿qué uplift es razonable? Default conservador: +30 % (18 % → 23,4 %).
                  </Text>
                </div>
              </Col>

              {/* Outputs */}
              <Col xs={24} md={14}>
                <Text strong style={{ fontSize: 12, color: '#1677ff', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 16 }}>
                  Resultado anual
                </Text>

                <Row gutter={[12, 12]}>
                  <Col xs={12}>
                    <div style={{ background: '#f6ffed', borderRadius: 10, padding: '14px 18px', border: '1px solid #b7eb8f' }}>
                      <Text style={{ fontSize: 11, color: '#389e0d', fontWeight: 600, display: 'block' }}>Horas liberadas</Text>
                      <Text style={{ fontSize: 22, fontWeight: 800, color: '#237804', display: 'block', lineHeight: 1.1, marginTop: 2 }}>
                        {Math.round(calc.recoveredHrs).toLocaleString('es-ES')} h/mes
                      </Text>
                      <Text style={{ fontSize: 11, color: '#595959' }}>= {calc.recoveredFTE.toFixed(1)} FTE equivalentes</Text>
                    </div>
                  </Col>
                  <Col xs={12}>
                    <div style={{ background: '#f6ffed', borderRadius: 10, padding: '14px 18px', border: '1px solid #b7eb8f' }}>
                      <Text style={{ fontSize: 11, color: '#389e0d', fontWeight: 600, display: 'block' }}>Nueva conversión</Text>
                      <Text style={{ fontSize: 22, fontWeight: 800, color: '#237804', display: 'block', lineHeight: 1.1, marginTop: 2 }}>
                        {fmtPct(calc.newConv * 100)}
                      </Text>
                      <Text style={{ fontSize: 11, color: '#595959' }}>vs {fmtPct(BUSINESS.conversion * 100)} actual</Text>
                    </div>
                  </Col>

                  <Col xs={12}>
                    <div style={{ background: '#fff', borderRadius: 10, padding: '14px 18px', border: '1px solid #d9d9d9' }}>
                      <Text style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 600, display: 'block' }}>+ Proyectos / año por conversión</Text>
                      <Text style={{ fontSize: 22, fontWeight: 800, color: '#141414', display: 'block', lineHeight: 1.1, marginTop: 2 }}>
                        +{calc.dProjConv.toFixed(0)}
                      </Text>
                      <Text style={{ fontSize: 11, color: '#52c41a', fontWeight: 600 }}>≈ +{fmtKEUR(calc.dRevConv * 1000)}</Text>
                    </div>
                  </Col>
                  <Col xs={12}>
                    <div style={{ background: '#fff', borderRadius: 10, padding: '14px 18px', border: '1px solid #d9d9d9' }}>
                      <Text style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 600, display: 'block' }}>+ Proyectos / año por capacidad</Text>
                      <Text style={{ fontSize: 22, fontWeight: 800, color: '#141414', display: 'block', lineHeight: 1.1, marginTop: 2 }}>
                        +{calc.dProjCap.toFixed(0)}
                      </Text>
                      <Text style={{ fontSize: 11, color: '#52c41a', fontWeight: 600 }}>≈ +{fmtKEUR(calc.dRevCap * 1000)}</Text>
                    </div>
                  </Col>

                  <Col xs={24}>
                    <div style={{
                      background: 'linear-gradient(135deg, #1677ff 0%, #003a8c 100%)',
                      borderRadius: 10, padding: '18px 22px', color: '#fff', marginTop: 4,
                    }}>
                      <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', fontWeight: 600, letterSpacing: 0.5, display: 'block' }}>
                        REVENUE INCREMENTAL ANUAL (steady state)
                      </Text>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginTop: 4 }}>
                        <Text style={{ fontSize: 34, fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                          +{fmtMEUR(calc.dRevTotal * 1000)}
                        </Text>
                        <Tag style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', fontWeight: 700, fontSize: 12 }}>
                          +{fmtPct(calc.growthPct)} sobre {fmtMEUR(BUSINESS.revenueKEUR)}
                        </Tag>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        </Card>

        {/* ── PALANCA B — Moat ──────────────────────────────────────────────── */}
        <Card
          style={{ marginBottom: 28, borderRadius: 16, border: '1px solid #efdbff', overflow: 'hidden' }}
          styles={{ body: { padding: 0 } }}
        >
          <div style={{ background: 'linear-gradient(135deg, #f9f0ff 0%, #f5f0ff 100%)', padding: '24px 32px', borderBottom: '1px solid #efdbff' }}>
            <Space size={10} align="center">
              <div style={{ width: 36, height: 36, borderRadius: 9, background: '#722ed1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrophyOutlined style={{ color: '#fff', fontSize: 17 }} />
              </div>
              <div>
                <Text style={{ fontSize: 11, fontWeight: 700, color: '#722ed1', letterSpacing: 0.6, display: 'block' }}>PALANCA B</Text>
                <Title level={3} style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#22075e' }}>Ventaja competitiva que compone</Title>
              </div>
            </Space>
          </div>

          <div style={{ padding: '28px 32px' }}>
            <Paragraph style={{ fontSize: 14, color: '#595959', lineHeight: 1.75, marginBottom: 24 }}>
              El upside de capacidad lo pueden replicar contratando o comprando software off-the-shelf. <strong style={{ color: '#141414' }}>El moat no.</strong> Cada presupuesto, cada reunión, cada incidencia en obra alimenta una memoria que aprende del propio negocio de Cubicup. Lo que un competidor necesitaría no es la tecnología — son los <strong style={{ color: '#141414' }}>datos acumulados</strong>.
            </Paragraph>

            <Row gutter={[20, 16]}>
              {[
                {
                  icon: <DatabaseOutlined />, color: '#722ed1',
                  title: 'Memoria que aprende del negocio',
                  body: 'Cada presupuesto aprobado mejora el siguiente. Cada incidencia en obra entrena al sistema para detectar patrones en proyectos futuros. La calidad de las decisiones aumenta solas con el tiempo.',
                },
                {
                  icon: <LockOutlined />, color: '#722ed1',
                  title: 'Datos propios, no API de terceros',
                  body: 'Cubicup controla el dato — no lo entrega a OpenAI ni a Microsoft. Conecta con la API propia o cualquier MCP. Total privacidad y soberanía técnica.',
                },
                {
                  icon: <RocketOutlined />, color: '#722ed1',
                  title: 'Onboarding en días, no meses',
                  body: 'Un nuevo Construction Manager pregunta al Brain "¿cómo gestionamos visitas de obra?" y obtiene procedimientos, ejemplos reales y contactos. Reduce dependencia de personas concretas.',
                },
                {
                  icon: <BulbOutlined />, color: '#722ed1',
                  title: 'Cada cliente entrena al siguiente',
                  body: 'La cualificación de un lead se beneficia de los 110 proyectos ya cerrados. El presupuesto de una reforma de 80 m² consulta las 30 reformas similares previas. El conocimiento compone.',
                },
              ].map((m, i) => (
                <Col key={i} xs={24} md={12}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f9f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: m.color, fontSize: 14 }}>
                      {m.icon}
                    </div>
                    <div>
                      <Text strong style={{ display: 'block', fontSize: 14, color: '#22075e', marginBottom: 4 }}>{m.title}</Text>
                      <Text style={{ fontSize: 12.5, color: '#595959', lineHeight: 1.65 }}>{m.body}</Text>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>

            <div style={{ marginTop: 28, padding: '16px 22px', background: '#22075e', borderRadius: 10 }}>
              <Text style={{ color: '#fff', fontSize: 14, fontStyle: 'italic', lineHeight: 1.65 }}>
                "Mientras la competencia sigue trabajando con Excel y WhatsApp, Cubicup tendrá una IA propietaria que aprendió del negocio durante 2 años. No es una feature — es una <strong style={{ color: '#d3adf7' }}>barrera de entrada</strong>."
              </Text>
            </div>
          </div>
        </Card>

        {/* ── PROYECCIÓN 3 AÑOS ─────────────────────────────────────────────── */}
        <Card
          style={{ marginBottom: 28, borderRadius: 16, border: '1px solid #f0f0f0' }}
          styles={{ body: { padding: '28px 32px' } }}
        >
          <Space size={10} align="center" style={{ marginBottom: 20 }}>
            <LineChartOutlined style={{ fontSize: 18, color: '#1677ff' }} />
            <Title level={3} style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Proyección 3 años</Title>
            <Tag color="blue" style={{ fontWeight: 600, fontSize: 11 }}>Escenario steady-state actual</Tag>
          </Space>

          <Paragraph style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 24 }}>
            Asumiendo ramp-up del Brain (50 % del upside en año 1, 85 % en año 2, 100 % en año 3). No incluye crecimiento orgánico del mercado.
          </Paragraph>

          <Row gutter={16}>
            {[
              { year: 'Año 1', delta: calc.dRevYear1KEUR, ramp: '50% capacidad', total: BUSINESS.revenueKEUR + calc.dRevYear1KEUR },
              { year: 'Año 2', delta: calc.dRevYear2KEUR, ramp: '85% capacidad', total: BUSINESS.revenueKEUR + calc.dRevYear2KEUR },
              { year: 'Año 3', delta: calc.dRevYear3KEUR, ramp: '100% steady-state', total: BUSINESS.revenueKEUR + calc.dRevYear3KEUR },
            ].map((y, i) => (
              <Col key={i} xs={24} md={8}>
                <Card
                  style={{ borderRadius: 12, border: '1px solid #e6f4ff', background: i === 2 ? 'linear-gradient(135deg, #f0f7ff, #e6f4ff)' : '#fafafa' }}
                  styles={{ body: { padding: '20px 24px' } }}
                >
                  <Text strong style={{ fontSize: 11, color: '#1677ff', letterSpacing: 0.5, textTransform: 'uppercase', display: 'block' }}>{y.year}</Text>
                  <Text style={{ fontSize: 10, color: '#8c8c8c', display: 'block', marginBottom: 14 }}>{y.ramp}</Text>

                  <div>
                    <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block' }}>Revenue total</Text>
                    <Text style={{ fontSize: 32, fontWeight: 800, color: '#141414', lineHeight: 1.05, display: 'block' }}>
                      {fmtMEUR(y.total)}
                    </Text>
                  </div>

                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                    <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block' }}>Incremental vs hoy</Text>
                    <Text strong style={{ fontSize: 18, color: '#52c41a' }}>+{fmtMEUR(y.delta)}</Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          <div style={{ marginTop: 20, padding: '14px 20px', background: '#f6ffed', borderRadius: 10, border: '1px solid #b7eb8f', display: 'flex', alignItems: 'center', gap: 12 }}>
            <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />
            <Text style={{ fontSize: 14, color: '#262626' }}>
              <strong>Revenue incremental acumulado a 3 años: {fmtMEUR(calc.cumRev3y)}</strong>
            </Text>
          </div>
        </Card>

        {/* ── CAPEX + OPEX ──────────────────────────────────────────────────── */}
        <Card
          style={{ marginBottom: 28, borderRadius: 16, border: '1px solid #f0f0f0' }}
          styles={{ body: { padding: '28px 32px' } }}
        >
          <Space size={10} align="center" style={{ marginBottom: 20 }}>
            <ThunderboltOutlined style={{ fontSize: 18, color: '#8c8c8c' }} />
            <Title level={3} style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Coste — el detalle menos importante</Title>
          </Space>

          <Paragraph style={{ fontSize: 13, color: '#595959', marginBottom: 24, lineHeight: 1.7 }}>
            El OPEX mensual del Brain es <strong>{fmtPct(calc.opexAsCMPct)} del salario de UN Construction Manager</strong>. Si el escenario más conservador genera +{fmtMEUR(calc.dRevTotal * 1000)} año, el coste es ruido contable.
          </Paragraph>

          <Row gutter={24}>
            <Col xs={24} md={14}>
              <Text strong style={{ fontSize: 12, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 12 }}>
                OPEX mensual (infraestructura)
              </Text>

              <div style={{ border: '1px solid #f0f0f0', borderRadius: 10, overflow: 'hidden' }}>
                {OPEX_MONTHLY.map((o, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', padding: '9px 16px',
                    borderBottom: i < OPEX_MONTHLY.length - 1 ? '1px solid #f5f5f5' : 'none',
                    background: i % 2 === 0 ? '#fff' : '#fafafa',
                  }}>
                    <Text style={{ fontSize: 12.5, color: '#262626' }}>{o.item}</Text>
                    <Text style={{ fontSize: 12.5, color: '#262626', fontWeight: 500 }}>{fmtEUR(o.value)}</Text>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: '#262626' }}>
                  <Text strong style={{ fontSize: 13, color: '#fff' }}>Total OPEX</Text>
                  <Text strong style={{ fontSize: 13, color: '#fff' }}>{fmtEUR(OPEX_TOTAL_MONTH)} / mes · {fmtEUR(calc.opexYear)} / año</Text>
                </div>
              </div>
            </Col>

            <Col xs={24} md={10}>
              <Text strong style={{ fontSize: 12, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 12 }}>
                CAPEX y comparativa
              </Text>

              <div style={{ background: '#fafafa', borderRadius: 10, padding: '16px 20px', border: '1px solid #f0f0f0', marginBottom: 12 }}>
                <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block' }}>CAPEX desarrollo (Claude Code)</Text>
                <Text style={{ fontSize: 20, fontWeight: 700, color: '#262626', display: 'block', marginTop: 2 }}>~30.000 €</Text>
                <Text style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4, display: 'block', lineHeight: 1.55 }}>
                  Inversión inicial ya parcialmente realizada. Claude Code acelera 5–10× respecto a desarrollo tradicional.
                </Text>
              </div>

              <div style={{ background: '#fff7e6', borderRadius: 10, padding: '16px 20px', border: '1px solid #ffd591' }}>
                <Text style={{ fontSize: 11, color: '#d46b08', display: 'block', fontWeight: 600 }}>Coste anual total (CAPEX amortizado + OPEX)</Text>
                <Text style={{ fontSize: 24, fontWeight: 800, color: '#d46b08', display: 'block', marginTop: 2, lineHeight: 1.05 }}>
                  {fmtEUR(calc.opexYear + 10000)}
                </Text>
                <Text style={{ fontSize: 11, color: '#595959', marginTop: 6, display: 'block', lineHeight: 1.55 }}>
                  vs <strong>1 Construction Manager: 39.600 €/año</strong>. Por menos de medio sueldo, multiplicas la capacidad de los 35.
                </Text>
              </div>
            </Col>
          </Row>
        </Card>

        {/* ── CTA ────────────────────────────────────────────────────────────── */}
        <Card
          style={{
            marginBottom: 32, borderRadius: 16,
            background: 'linear-gradient(135deg, #fffbe6 0%, #fff7e6 100%)',
            border: '1px solid #ffd591',
          }}
          styles={{ body: { padding: '28px 32px' } }}
        >
          <Space size={14} align="start">
            <WarningOutlined style={{ color: '#fa8c16', fontSize: 24, marginTop: 4 }} />
            <div>
              <Title level={4} style={{ margin: 0, fontSize: 18, color: '#614700', marginBottom: 6 }}>
                La pregunta no es "¿cuánto cuesta?"
              </Title>
              <Text style={{ fontSize: 14, color: '#262626', lineHeight: 1.75 }}>
                Es <strong>"¿qué pasa si nuestra competencia construye esto antes que nosotros?"</strong> En 24 meses, los datos acumulados son la barrera. El competidor que arranque tarde necesitará no solo la tecnología — necesitará 110 proyectos de histórico, miles de presupuestos aprobados y cientos de reuniones procesadas. Eso no se compra ni se programa.
              </Text>
            </div>
          </Space>
        </Card>

        {/* ── FOOTNOTES ─────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 40, padding: '20px 24px', background: '#fafafa', borderRadius: 10, border: '1px solid #f0f0f0' }}>
          <Text strong style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 10 }}>
            Asunciones y fuentes
          </Text>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 11.5, color: '#595959', lineHeight: 1.75 }}>
            <li>Equipo 35 personas · coste empresa medio 1,65 M€/año (salario bruto × 1,32 para incluir SS empresa).</li>
            <li>Negocio: 11 M€ revenue, 110 proyectos × 100 K€ ticket medio, conversión 18 % (611 leads/año).</li>
            <li>Tiempo de búsqueda: McKinsey Global Institute — knowledge workers dedican 1,8 h/día (≈25 % del tiempo) a buscar y agregar información.</li>
            <li>OPEX calculado con tarifas oficiales de Google Cloud Run, Cloud SQL, MongoDB Atlas M10, Anthropic Claude API, Vertex AI (Gemini), LangSmith Plus y Tavily — uso estimado de uso intensivo en agentes.</li>
            <li>Defaults conservadores: recuperar 60 % del tiempo de búsqueda y +30 % conversión. Ambos sliders editables.</li>
          </ul>
        </div>

      </div>
    </div>
  );
};

// ── Standalone page (used by /business-case route) ───────────────────────────

const BusinessCasePage: React.FC = () => (
  <Layout style={{ height: '100vh', background: 'transparent' }}>
    <BusinessCaseContent />
  </Layout>
);

export default BusinessCasePage;
